import React from 'react';

import { css } from '@compiled/react';

import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';
import type { DataEventProps } from '@atlassian/wac-design/types/data-event-props';

import { Link } from '@/libraries/design/components/link/src/link';
import { wacToken } from '@/libraries/design/foundations';
import type { RichTextComponentProps } from '@/libraries/design/utils/rich-text/src/rich-text';
import { CheckmarkCircle } from '@/libraries/wpl/foundations/src/icons/basic/checkmark-circle';

import { AtlassianIcon } from './atlassian-icon';
import { ErrorIcon } from './error-icon';
import { ProTipIcon } from './pro-tip-icon';

export type PanelType = 'pro-tip' | 'atlassian-pro-tip' | 'highlight';

const iconMap: Record<PanelType, React.ReactElement> = {
	'pro-tip': <ProTipIcon />,
	'atlassian-pro-tip': <AtlassianIcon />,
	highlight: <ErrorIcon />,
};

export interface PanelProps {
	panelType: PanelType;
	label: string;
	description: string | React.ReactElement<RichTextComponentProps>;
}

export interface PanelInlineLink {
	text: string;
	href: string;
	dataEventProps?: DataEventProps;
}

export interface InlineLinkPanelProps {
	panelType: 'inline-link';
	description: string;
	inlineLink: PanelInlineLink;
}

const addSectionTitleClass = (text: string) =>
	text.replace(/<p><b>([A-Z0-9\s:]+)<\/b><\/p>/g, '<p class="panel-section-title"><b>$1</b></p>');

const isWordChar = (char: string | undefined) => !!char && /[\p{L}\p{N}]/u.test(char);

// Prefers a whole-word match so "IT" doesn't link inside "ITSM". Falls back to the first
// substring match for scripts written without spaces (e.g. Japanese), where nothing is a
// whole word.
const findLinkText = (description: string, linkText: string) => {
	const haystack = description.toLowerCase();
	const needle = linkText.toLowerCase();
	const first = haystack.indexOf(needle);
	for (let index = first; index !== -1; index = haystack.indexOf(needle, index + 1)) {
		if (!isWordChar(description[index - 1]) && !isWordChar(description[index + needle.length])) {
			return index;
		}
	}
	return first;
};

const splitAroundLinkText = (description: string, linkText: string) => {
	const index = findLinkText(description, linkText);
	if (index === -1) {
		const copy = description.trimEnd();
		return { before: copy ? `${copy} ` : '', match: linkText, after: '' };
	}
	return {
		before: description.slice(0, index),
		match: description.slice(index, index + linkText.length),
		after: description.slice(index + linkText.length),
	};
};

const InlineLinkPanel = ({ description, inlineLink }: InlineLinkPanelProps) => {
	const linkText = inlineLink.text.trim();
	const { before, match, after } = linkText
		? splitAroundLinkText(description, linkText)
		: { before: description, match: '', after: '' };
	return (
		<div css={inlineLinkContainerCss}>
			<span css={inlineLinkIconCss} aria-hidden="true">
				<CheckmarkCircle size="100%" />
			</span>
			<p className="body-lg" css={inlineLinkBodyCss}>
				{before}
				{/* Empty link text would render an anchor with no accessible name, so skip the link. */}
				{match && (
					// `aria-label` is required by the a11y lint rules (wac/ldr/0024); `Link` renders `text`.
					<Link
						type="inline"
						text={match}
						aria-label={match}
						href={inlineLink.href}
						showArrow={false}
						className=""
						dataEventProps={inlineLink.dataEventProps}
					/>
				)}
				{after}
			</p>
		</div>
	);
};

export const Panel = (props: PanelProps | InlineLinkPanelProps) => {
	if (props.panelType === 'inline-link') {
		return <InlineLinkPanel {...props} />;
	}
	const descriptionComponent =
		typeof props.description === 'string' ? (
			<p>{props.description}</p>
		) : React.isValidElement<RichTextComponentProps>(props.description) ? (
			React.cloneElement(props.description, {
				...(props.description.props.text !== undefined
					? { text: addSectionTitleClass(props.description.props.text ?? '') }
					: {}),
			})
		) : (
			(props.description ?? null)
		);
	const icon = iconMap[props.panelType];

	return (
		<div
			css={[
				panelContainerCss,
				css({
					backgroundColor:
						props.panelType === 'atlassian-pro-tip'
							? wacToken('color.extended.blue100')
							: wacToken('color.neutral.light200'),
				}),
			]}
		>
			<div>{icon}</div>
			<div css={textCss}>
				<p className="eyebrow-lg">{props.label}</p>
				{descriptionComponent}
			</div>
		</div>
	);
};

const panelContainerCss = css({
	borderRadius: wacToken('corner.radius.card'),
	paddingBlockStart: token('space.200'),
	paddingBlockEnd: token('space.250'),
	paddingInline: token('space.400'),
	display: 'flex',
	gap: token('space.200'),

	[media.below.sm]: {
		flexDirection: 'column',
		gap: token('space.050'),
	},
});

const textCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['p']: {
		margin: 0,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['p.eyebrow-lg']: {
		marginBlockEnd: token('space.100'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['p.panel-section-title + p']: {
		marginBlockStart: token('space.100'),
	},
});

const inlineLinkContainerCss = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.200'),
	borderRadius: wacToken('corner.radius.card'),
	backgroundColor: wacToken('color.extended.blue100'),
	paddingBlock: token('space.300'),
	paddingInline: token('space.400'),

	[media.below.md]: {
		paddingBlock: token('space.200'),
		paddingInline: token('space.250'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& .body-lg': {
			fontSize: '1rem',
		},
	},
	[media.below.sm]: {
		paddingInline: token('space.200'),
	},
});

const inlineLinkIconCss = css({
	display: 'block',
	flexShrink: 0,
	width: '24px',
	height: '24px',
	lineHeight: 0,
	color: wacToken('color.extended.blue700'),

	[media.below.md]: {
		width: '16px',
		height: '16px',
	},
});

const inlineLinkBodyCss = css({
	flex: 1,
	minWidth: 0,
	margin: 0,
});
